/*
 * iter-126 — Shared PWA install / update controller.
 *
 * The BeforeInstallPromptEvent can only be captured once per page life,
 * and it's the same event a user might click "Install" on later from
 * either the floating banner OR the user-menu dropdown. This module
 * hangs the deferred prompt on a module-level singleton so BOTH UIs
 * can share the SAME captured event.
 *
 * "Dismiss" is now PERMANENT — once the user says "not now" on the
 * banner we never show it again. They can still invoke installation
 * from the dropdown at any time.
 */

import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "planflow.pwa.install.dismissed";

let _deferredPrompt = null;
const _listeners = new Set();

function _notify() {
    for (const cb of _listeners) {
        try { cb(_deferredPrompt); } catch { /* noop */ }
    }
}

// Attach once on module load. Guarded so hot-reload doesn't double-bind.
if (typeof window !== "undefined" && !window.__planflowInstallBound) {
    window.__planflowInstallBound = true;
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        _deferredPrompt = e;
        _notify();
    });
    window.addEventListener("appinstalled", () => {
        _deferredPrompt = null;
        _notify();
    });
}

export function isStandalone() {
    if (typeof window === "undefined") return false;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone) return true;
    return false;
}

export function isIos() {
    if (typeof window === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function wasDismissed() {
    try {
        return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
        return false;
    }
}

export function markDismissed() {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
}

/**
 * Fires the browser's install prompt (if we captured one). Returns the
 * user's choice string ("accepted" or "dismissed") or "unavailable" if
 * the prompt can't be shown (iOS, already installed, or never fired).
 */
export async function promptInstall() {
    if (!_deferredPrompt) return "unavailable";
    try {
        _deferredPrompt.prompt();
        const choice = await _deferredPrompt.userChoice;
        _deferredPrompt = null;
        _notify();
        return (choice && choice.outcome) || "dismissed";
    } catch {
        _deferredPrompt = null;
        _notify();
        return "dismissed";
    }
}

/** React hook — returns { canInstall, installed, promptInstall } and
 *  re-renders when the deferred prompt appears/disappears. */
export function useInstallPrompt() {
    const [deferred, setDeferred] = useState(_deferredPrompt);
    const [installed, setInstalled] = useState(isStandalone());

    useEffect(() => {
        const cb = (dp) => {
            setDeferred(dp);
            setInstalled(isStandalone());
        };
        _listeners.add(cb);
        return () => _listeners.delete(cb);
    }, []);

    const prompt = useCallback(() => promptInstall(), []);

    return {
        canInstall: !!deferred || isIos(),
        installed,
        deferred,
        isIos: isIos(),
        promptInstall: prompt,
    };
}
